import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  PICK_SHEET_COLUMN_LABEL_MAX,
  PICK_SHEET_EXTRA_COLUMNS_MAX,
  type PickSheetSettings,
} from "@shared/schema";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

type BlankColumn = { key: number; label: string; width?: number };

/**
 * Settings → Admin → Pick Sheet: what the printed pick sheet shows beyond
 * Qty | Item | [ X ] (organizations.pick_sheet_settings; the document is built by
 * server/pickSheet.ts). With everything off it prints exactly the sheet it always has.
 */
export function PickSheetSettingsCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Keyed by the org-scoped path, so one org's cached settings never show under another's.
  const settingsKey = [getApiPath("/api/pick-sheet-settings")];
  const { data: saved, isLoading, isError, refetch, isRefetching } = useQuery<PickSheetSettings>({ queryKey: settingsKey });

  const [unitColumn, setUnitColumn] = useState(false);
  const [fulfillmentDate, setFulfillmentDate] = useState(false);
  const [city, setCity] = useState(false);
  const [columns, setColumns] = useState<BlankColumn[]>([]);
  const [dirty, setDirty] = useState(false);
  const nextKey = useRef(0);

  useEffect(() => {
    if (!saved) return;
    setUnitColumn(saved.showUnitColumn === true);
    setFulfillmentDate(saved.showFulfillmentDate === true);
    setCity(saved.showCity === true);
    setColumns((saved.extraColumns ?? []).map((c) => ({ key: nextKey.current++, label: c.label, width: c.width })));
    setDirty(false);
  }, [saved]);

  const save = useMutation({
    mutationFn: async (settings: PickSheetSettings): Promise<PickSheetSettings> => {
      const res = await apiRequest("PUT", "/api/pick-sheet-settings", settings);
      return res.json();
    },
    onSuccess: (stored) => {
      queryClient.setQueryData(settingsKey, stored);
      toast({ title: "Pick sheet saved", description: "The next pick sheet you print uses it." });
    },
    onError: (error) => {
      toast({
        title: "Couldn't save the pick sheet",
        description: parseApiError(error).message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  const change = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setDirty(true);
  };

  const blankLabel = columns.some((c) => c.label.trim() === "");
  const rowPreview = [
    "Qty",
    ...(unitColumn ? ["Unit"] : []),
    "Item",
    ...columns.map((c) => c.label.trim() || "…"),
    "[ X ]",
  ].join("  |  ");

  const submit = () => {
    save.mutate({
      showCity: city,
      showUnitColumn: unitColumn,
      showFulfillmentDate: fulfillmentDate,
      extraColumns: columns.map((c) => (c.width === undefined ? { label: c.label.trim() } : { label: c.label.trim(), width: c.width })),
    });
  };

  const toggles: Array<{ id: string; label: string; help: string; checked: boolean; onChange: (v: boolean) => void }> = [
    {
      id: "pick-sheet-unit-column",
      label: "Unit column",
      help: "A Unit column after Qty, from each product's sell unit (case, each, lb).",
      checked: unitColumn,
      onChange: change(setUnitColumn),
    },
    {
      id: "pick-sheet-fulfillment-date",
      label: "Delivery or pickup day in the header",
      help: "Instead of the day the order was placed.",
      checked: fulfillmentDate,
      onChange: change(setFulfillmentDate),
    },
    {
      id: "pick-sheet-city",
      label: "Customer's town in the header",
      help: "After the customer's name, from their address. Customers with no address print as before.",
      checked: city,
      onChange: change(setCity),
    },
  ];

  return (
    <Card data-testid="card-pick-sheet-settings">
      <CardHeader className="border-b border-gray-100 bg-gray-50/50">
        <div>
          <CardTitle>Pick Sheet</CardTitle>
          <CardDescription className="mt-[5px] mb-[5px]">
            What the printed pick sheet shows. With everything off it prints Qty, Item and a check box, as it always has.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : isError || !saved ? (
          // Never show editable defaults in place of settings that failed to load: saving
          // them would overwrite what is stored.
          <div className="flex items-center justify-between gap-4" data-testid="pick-sheet-settings-load-error">
            <p className="text-sm text-red-600">Couldn't load the pick-sheet settings.</p>
            <Button type="button" variant="outline" size="sm" disabled={isRefetching} onClick={() => refetch()}>
              {isRefetching && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Try again
            </Button>
          </div>
        ) : (
          <>
            {toggles.map((toggle) => (
              <div key={toggle.id} className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor={toggle.id}>{toggle.label}</Label>
                  <p className="text-sm text-gray-500">{toggle.help}</p>
                </div>
                <Switch id={toggle.id} data-testid={`switch-${toggle.id}`} checked={toggle.checked} onCheckedChange={toggle.onChange} />
              </div>
            ))}

            <div className="space-y-2">
              <div className="space-y-1">
                <Label>Blank columns</Label>
                <p className="text-sm text-gray-500">
                  Empty columns to write in by hand, such as a lot or receipt code. Up to {PICK_SHEET_EXTRA_COLUMNS_MAX}.
                </p>
              </div>
              {columns.map((column, index) => (
                <div key={column.key} className="flex items-center gap-2">
                  <Input
                    value={column.label}
                    maxLength={PICK_SHEET_COLUMN_LABEL_MAX}
                    placeholder="e.g., Code"
                    aria-label={`Name of blank column ${index + 1}`}
                    data-testid={`input-pick-sheet-column-${index}`}
                    className="max-w-xs"
                    onChange={(e) => {
                      const label = e.target.value;
                      setColumns((prev) => prev.map((c) => (c.key === column.key ? { ...c, label } : c)));
                      setDirty(true);
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove blank column ${index + 1}`}
                    data-testid={`button-remove-pick-sheet-column-${index}`}
                    onClick={() => {
                      setColumns((prev) => prev.filter((c) => c.key !== column.key));
                      setDirty(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              ))}
              {blankLabel && <p className="text-sm text-red-600">Name each column, or remove it.</p>}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={columns.length >= PICK_SHEET_EXTRA_COLUMNS_MAX}
                data-testid="button-add-pick-sheet-column"
                onClick={() => {
                  setColumns((prev) => [...prev, { key: nextKey.current++, label: "" }]);
                  setDirty(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add a column
              </Button>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600" data-testid="text-pick-sheet-row-preview">
                Each line: <span className="font-medium text-gray-900">{rowPreview}</span>
              </p>
              <Button
                type="button"
                className="bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green"
                disabled={!dirty || blankLabel || save.isPending}
                data-testid="button-save-pick-sheet"
                onClick={submit}
              >
                {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
